import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const LegalNamingCalculator = () => {
  const [legalData, setLegalData] = useState({
    entityType: '',
    jurisdiction: '',
    purpose: '',
    ownerName: '',
    businessType: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLegalData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const generateLegalName = () => {
    const { entityType, jurisdiction, purpose, ownerName, businessType } = legalData;
    
    if (!entityType.trim() || !jurisdiction.trim() || !businessType.trim()) {
      setError('Please enter all required fields');
      return;
    }

    // Generate legal name components
    const legalNameComponents = {
      fullLegalName: `${ownerName ? `${ownerName} ` : ''}${businessType} ${entityType}${purpose ? ` - ${purpose}` : ''} (${jurisdiction})`,
      shortName: `${businessType} ${entityType}`,
      legalIdentity: `${businessType} ${entityType} (${jurisdiction})`,
      registrationName: `${ownerName ? `${ownerName} ` : ''}${businessType} ${entityType}`,
      officialName: `${businessType} ${entityType}${purpose ? ` - ${purpose}` : ''}`
    };

    // Generate legal naming rules
    const namingRules = {
      entityType: 'Entity type should be accurate and officially recognized (e.g., LLC, Inc., Ltd.)',
      jurisdiction: 'Jurisdiction should specify the legal authority and location',
      purpose: 'Purpose should clearly define the business objective',
      ownerName: 'Owner name should be the legal name of the business owner',
      businessType: 'Business type should accurately describe the nature of the business'
    };

    // Generate practical example usage
    const exampleUsage = {
      registration: `Use ${legalNameComponents.fullLegalName} in all legal documents and registrations.`,
      contracts: `Use ${legalNameComponents.legalIdentity} in contracts and agreements.`,
      banking: `Use ${legalNameComponents.registrationName} for banking and financial transactions.`,
      marketing: `Use ${legalNameComponents.officialName} in marketing materials and public communications.`,
      compliance: `Use ${legalNameComponents.fullLegalName} in compliance documents and regulatory filings.`
    };

    // Generate detailed calculation steps
    const calculationSteps = [
      'Step 1: Analyze Legal Components',
      `Entity Type: ${entityType} (e.g., LLC, Inc., Ltd.)`,
      `Jurisdiction: ${jurisdiction} (Legal authority and location)`,
      `Purpose: ${purpose || 'None'} (Business objective)`,
      `Owner Name: ${ownerName || 'None'} (Legal name of owner)`,
      `Business Type: ${businessType} (Nature of business)`,
      '\nStep 2: Create Legal Identity',
      `Full Legal Name: ${legalNameComponents.fullLegalName}`,
      `Short Name: ${legalNameComponents.shortName}`,
      `Legal Identity: ${legalNameComponents.legalIdentity}`,
      `Registration Name: ${legalNameComponents.registrationName}`,
      `Official Name: ${legalNameComponents.officialName}`,
      '\nStep 3: Apply Naming Rules',
      `Entity Type: ${namingRules.entityType}`,
      `Jurisdiction: ${namingRules.jurisdiction}`,
      `Purpose: ${namingRules.purpose}`,
      `Owner Name: ${namingRules.ownerName}`,
      `Business Type: ${namingRules.businessType}`
    ];

    // Generate enhanced chart data
    const chartData = {
      bar: {
        labels: ['Entity Type', 'Jurisdiction', 'Purpose', 'Owner Name', 'Business Type'],
        datasets: [{
          label: 'Legal Components',
          data: [1, 1, purpose ? 1 : 0, ownerName ? 1 : 0, 1],
          backgroundColor: [
            'rgba(75, 192, 192, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            purpose ? 'rgba(255, 99, 132, 0.5)' : 'rgba(0, 0, 0, 0)',
            ownerName ? 'rgba(153, 102, 255, 0.5)' : 'rgba(0, 0, 0, 0)',
            'rgba(255, 159, 64, 0.5)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(54, 162, 235, 1)',
            purpose ? 'rgba(255, 99, 132, 1)' : 'rgba(0, 0, 0, 0)',
            ownerName ? 'rgba(153, 102, 255, 1)' : 'rgba(0, 0, 0, 0)',
            'rgba(255, 159, 64, 1)'
          ],
          borderWidth: 1
        }]
      },
      pie: {
        labels: ['Entity Type', 'Jurisdiction', 'Purpose', 'Owner Name', 'Business Type'],
        datasets: [{
          data: [1, 1, purpose ? 1 : 0, ownerName ? 1 : 0, 1],
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 99, 132, 0.7)',
            purpose ? 'rgba(255, 159, 64, 0.7)' : 'rgba(0, 0, 0, 0)',
            ownerName ? 'rgba(153, 102, 255, 0.7)' : 'rgba(0, 0, 0, 0)',
            'rgba(75, 192, 192, 0.7)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            purpose ? 'rgba(255, 159, 64, 1)' : 'rgba(0, 0, 0, 0)',
            ownerName ? 'rgba(153, 102, 255, 1)' :  'rgba(75, 192, 192, 1)',  
                      ],
                      borderWidth: 1
                    }]
                  }
                };
            
                const newResults = {
                  legalNameComponents,
                  namingRules,
                  exampleUsage,
                  calculationSteps
                };
            
                setResults(newResults);
                setChartData(chartData);
              };
            
              return (
                <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                  <Helmet>
                    <title>Legal Naming Calculator | Generate Legal Entity Names</title>
                    <meta name="description" content="Generate legal entity names with our Legal Naming Calculator. Get accurate naming conventions for businesses and organizations." />
                    <meta name="keywords" content="legal naming, business naming, entity naming, LLC, Inc., Ltd." />
                    <meta property="og:title" content="Legal Naming Calculator | Generate Legal Entity Names" />
                    <meta property="og:description" content="Generate legal entity names with our Legal Naming Calculator. Get accurate naming conventions for businesses and organizations." />
                    <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
                    <meta property="og:url" content="https://yourdomain.com/legal-naming-calculator" />
                    <meta property="og:type" content="website" />
                  </Helmet>
            
                  <div className="calculator-header mb-12">
                    <div className="flex items-center gap-4">
                      <div className="header-icon text-4xl">⚖️</div>
                      <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Legal Naming Calculator</h1>
                        <p className="text-lg text-blue-600 font-semibold">Generate Accurate Legal Entity Names</p>
                      </div>
                    </div>
                  </div>
            
                  <div className="calculator-description space-y-4 mb-12">
                    <p className="text-gray-700">
                      This calculator helps you generate legal entity names using standardized naming conventions. It provides naming rules and examples for different legal components.
                    </p>
                  </div>
            
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
                  >
                    <div className="input-group mb-6">
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="entityType">
                        Entity Type:
                      </label>
                      <input
                        type="text"
                        id="entityType"
                        name="entityType"
                        value={legalData.entityType}
                        onChange={handleInputChange}
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        placeholder="Enter entity type (e.g., LLC, Inc., Ltd.)"
                      />
                    </div>
            
                    <div className="input-group mb-6">
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="jurisdiction">
                        Jurisdiction:
                      </label>
                      <input
                        type="text"
                        id="jurisdiction"
                        name="jurisdiction"
                        value={legalData.jurisdiction}
                        onChange={handleInputChange}
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        placeholder="Enter jurisdiction (e.g., California, USA)"
                      />
                    </div>
            
                    <div className="input-group mb-6">
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="purpose">
                        Purpose (optional):
                      </label>
                      <input
                        type="text"
                        id="purpose"
                        name="purpose"
                        value={legalData.purpose}
                        onChange={handleInputChange}
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        placeholder="Enter purpose (e.g., Business, Corporation)"
                      />
                    </div>
                    
                    <div className="input-group mb-6">
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="entityType">
                        Entity Type:
                      </label>
                      <input
                        type="text"
                        id="entityType"
                        name="entityType"
                        value={legalData.entityType}
                        onChange={handleInputChange}
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                placeholder="Enter entity type (e.g., LLC, Inc., Ltd.)"
                                              />
                                            </div>
                        
                                            <div className="input-group mb-6">
                                              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="jurisdiction">
                                                Jurisdiction:
                                              </label>
                                              <input
                                                type="text"
                                                id="jurisdiction"
                                                name="jurisdiction"
                                                value={legalData.jurisdiction}
                                                onChange={handleInputChange}
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                placeholder="Enter jurisdiction (e.g., California, USA)"
                                              />
                                            </div>
                        
                                            <div className="input-group mb-6">
                                              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="purpose">
                                                Purpose (optional):
                                              </label>
                                              <input
                                                type="text"
                                                id="purpose"
                                                name="purpose"
                                                value={legalData.purpose}
                                                onChange={handleInputChange}
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                placeholder="Enter purpose (e.g., Consulting, Manufacturing)"
                                              />
                                            </div>
                        
                                            <div className="input-group mb-6">
                                              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="ownerName">
                                                Owner Name (optional):
                                              </label>
                                              <input
                                                type="text"
                                                id="ownerName"
                                                name="ownerName"
                                                value={legalData.ownerName}
                                                onChange={handleInputChange}
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                placeholder="Enter owner name"
                                              />
                                            </div>
                        
                                            <div className="input-group mb-6">
                                              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="businessType">
                                                Business Type:
                                              </label>
                                              <input
                                                type="text"
                                                id="businessType"
                                                name="businessType"
                                                value={legalData.businessType}
                                                onChange={handleInputChange}
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                placeholder="Enter business type (e.g., Consulting, Manufacturing)"
                                              />
                                            </div>
                        
                                            <button
                                              onClick={generateLegalName}
                                              className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
                                            >
                                              Generate Legal Name
                                            </button>
                        
                                            {error && (
                                              <div className="text-red-500 text-center mt-4">
                                                {error}
                                              </div>
                                            )}
                        
                                            {results && (
                                              <motion.div
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.5 }}
                                                className="calculator-section mt-12"
                                              >
                                                <div className="bg-white p-8 rounded-xl shadow-md">
                                                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
                                                  
                                                  <div className="grid grid-cols-1 gap-6 mb-12">
                                                    <div className="bg-white p-6 rounded-xl shadow-md">
                                                      <h4 className="text-xl font-semibold mb-4">Legal Name</h4>
                                                      <div className="space-y-4">
                                                        <div className="flex items-center justify-between border-b pb-3">
                                                          <span className="text-gray-600">Full Legal Name</span>
                                                          <span className="font-medium">{results.legalNameComponents.fullLegalName}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between border-b pb-3">
                                                          <span className="text-gray-600">Short Name</span>
                                                          <span className="font-medium">{results.legalNameComponents.shortName}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between border-b pb-3">
                                                          <span className="text-gray-600">Legal Identity</span>
                                                          <span className="font-medium">{results.legalNameComponents.legalIdentity}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between border-b pb-3">
                                                          <span className="text-gray-600">Registration Name</span>
                                                          <span className="font-medium">{results.legalNameComponents.registrationName}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                          <span className="text-gray-600">Official Name</span>
                                                          <span className="font-medium">{results.legalNameComponents.officialName}</span>
                                                        </div>
                                                      </div>
                                                    </div>
                        
                                                    <div className="bg-white p-6 rounded-xl shadow-md">
                                                      <h4 className="text-xl font-semibold mb-4">Naming Rules</h4>
                                                      <div className="space-y-4">
                                                        <div className="flex items-center justify-between border-b pb-3">
                                                          <span className="text-gray-600">Entity Type</span>
                                                          <span className="font-medium">{results.namingRules.entityType}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between border-b pb-3">
                                                          <span className="text-gray-600">Jurisdiction</span>
                                                          <span className="font-medium">{results.namingRules.jurisdiction}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between border-b pb-3">
                                                          <span className="text-gray-600">Purpose</span>
                                                          <span className="font-medium">{results.namingRules.purpose}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between border-b pb-3">
                                                          <span className="text-gray-600">Owner Name</span>
                                                          <span className="font-medium">{results.namingRules.ownerName}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                          <span className="text-gray-600">Business Type</span>
                                                          <span className="font-medium">{results.namingRules.businessType}</span>
                                                        </div>
                                                      </div>
                                                    </div>
                        
                                                    <div className="bg-white p-6 rounded-xl shadow-md">
                                                      <h4 className="text-xl font-semibold mb-4">Example Usage</h4>
                                                      <div className="space-y-4">
                                                        <div className="flex items-center justify-between border-b pb-3">
                                                          <span className="text-gray-600">Registration</span>
                                                          <span className="font-medium">{results.exampleUsage.registration}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between border-b pb-3">
                                                          <span className="text-gray-600">Contracts</span>
                                                          <span className="font-medium">{results.exampleUsage.contracts}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between border-b pb-3">
                                                          <span className="text-gray-600">Banking</span>
                                                          <span className="font-medium">{results.exampleUsage.banking}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between border-b pb-3">
                                                          <span className="text-gray-600">Marketing</span>
                                                          <span className="font-medium">{results.exampleUsage.marketing}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                          <span className="text-gray-600">Compliance</span>
                                                          <span className="font-medium">{results.exampleUsage.compliance}</span>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>
                        
                                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12">
                                                    <div>
                                                      <h4 className="text-xl font-semibold mb-4">Component Chart</h4>
                                                      <div className="h-96">
                                                        <Bar
                                                          data={chartData.bar}
                                                          options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            scales: {
                                                              y: {
                                                                beginAtZero: true,
                                                                title: {
                                                                  display: true,
                                                                  text: 'Component Level'
                                                                }
                                                              },
                                                              x: {
                                                                title: {
                                                                  display: true,
                                                                  text: 'Legal Component'
                                                                }
                                                              }
                                                            },
                                                            plugins: {
                                                              title: {
                                                                display: true,
                                                                text: 'Legal Component Levels'
                                                              }
                                                            }
                                                          }}
                                                        />
                                                      </div>
                                                    </div>
                        
                                                    <div>
                                                      <h4 className="text-xl font-semibold mb-4">Naming Chart</h4>
                                                      <div className="h-96">
                                                        <Pie
                                                          data={chartData.pie}
                                                          options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            plugins: {
                                                              title: {
                                                                display: true,
                                                                text: 'Legal Components'
                                                              }
                                                            }
                                                          }}
                                                        />
                                                      </div>
                                                    </div>
                                                  </div>
                        
                                                  <div className="mt-12 bg-gray-50 p-6 rounded-xl">
                                                    <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
                                                    <div className="space-y-4">
                                                      {results.calculationSteps.map((step, index) => (
                                                        <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                                                          <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  </div>
                                                </div>
                                              </motion.div>
                                            )}
                                          </motion.div>
                        
                                          <div className="calculator-section mt-24">
                                            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                                              How to Use Legal Naming Calculator <span className="ml-2 text-2xl">📋</span>
                                            </h2>
                                            <div className="bg-white p-8 rounded-xl shadow-md">
                                              <p className="text-gray-700 mb-6">
                                                Follow these steps to use the Legal Naming Calculator effectively:
                                              </p>
                                              
                                              <div className="space-y-8">
                                                <div>
                                                  <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Legal Information</h3>
                                                  <ul className="list-disc pl-8 space-y-2">
                                                    <li>Enter the entity type (required)</li>
                                                    <li>Input the jurisdiction (required)</li>
                                                    <li>Optionally add purpose</li>
                                                    <li>Optionally add owner name</li>
                                                    <li>Input business type (required)</li>
                                                  </ul>
                                                </div>
                                                
                                                <div>
                                                  <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Generate Legal Name</h3>
                                                  <ul className="list-disc pl-8 space-y-2">
                                                    <li>Click the "Generate Legal Name" button</li>
                                                    <li>Review the generated legal name</li>
                                                    <li>Check the naming rules</li>
                                                    <li>Verify the example usage</li>
                                                  </ul>
                                                </div>
                                                
                                                <div>
                                                  <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Review Results</h3>
                                                  <ul className="list-disc pl-8 space-y-2">
                                                    <li>Check the full legal name and short name</li>
                                                    <li>Review the naming rules for each component</li>
                                                    <li>Verify the example usage for different scenarios</li>
                                                  </ul>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                        
                                          <div className="calculator-section mt-24">
                                            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                                              Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
                                            </h2>
                                            <div className="bg-white p-8 rounded-xl shadow-md">
                                              <div className="space-y-8">
                                                <div className="border-l-4 border-blue-600 pl-4">
                                                  <h3 className="text-lg font-semibold mb-2">What is legal naming?</h3>
                                                  <p className="text-gray-700">
                                                    Legal naming is the process of creating standardized names for businesses and organizations according to legal requirements. It ensures proper identification and compliance with regulations.
                                                  </p>
                                                </div>
                        
                                                <div className="border-l-4 border-green-600 pl-4">
                                                  <h3 className="text-lg font-semibold mb-2">What components are required?</h3>
                                                  <p className="text-gray-700">
                                                    The minimum required components are:
                                                    <br/><br/>
                                                    - Entity Type: (e.g., LLC, Inc., Ltd.)
                                                    <br/>
                                                    - Jurisdiction: (Legal authority and location)
                                                    <br/>
                                                    - Business Type: (Nature of business)
                                                  </p>
                                                </div>
                        
                                                <div className="border-l-4 border-yellow-600 pl-4">
                                                  <h3 className="text-lg font-semibold mb-2">Why is legal naming important?</h3>
                                                  <p className="text-gray-700">
                                                    Legal naming helps in:
                                                    <br/><br/>
                                                    - Proper business identification
                                                    <br/>
                                                    - Compliance with legal requirements
                                                    <br/>
                                                    - Clear communication in contracts and agreements
                                                    <br/>
                                                    - Establishing legal identity
                                                  </p>
                                                </div>
                        
                                                <div className="border-l-4 border-purple-600 pl-4">
                                                  <h3 className="text-lg font-semibold mb-2">What is a jurisdiction?</h3>
                                                  <p className="text-gray-700">
                                                    A jurisdiction is the legal authority and location where the business is registered and operates. It determines the laws and regulations that apply to the business.
                                                  </p>
                                                </div>
                        
                                                <div className="border-l-4 border-red-600 pl-4">
                                                  <h3 className="text-lg font-semibold mb-2">What is a business type?</h3>
                                                  <p className="text-gray-700">
                                                    A business type describes the nature of the business, such as consulting, manufacturing, or retail. It helps in identifying the primary activities of the business.
                                                  </p>
                                                </div>
                        
                                                <div className="border-l-4 border-blue-600 pl-4">
                                                  <h3 className="text-lg font-semibold mb-2">How do I choose an entity type?</h3>
                                                  <p className="text-gray-700">
                                                    Common entity types include:
                                                    <br/><br/>
                                                    - LLC: Limited Liability Company
                                                    <br/>
                                                    - Inc.: Incorporated
                                                    <br/>
                                                    - Ltd.: Limited
                                                    <br/>
                                                    - Corp.: Corporation
                                                    <br/>
                                                    - LLP: Limited Liability Partnership
                                                  </p>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                        
                                          <div className="calculator-section mt-24">
                                            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                                              Features & Benefits <span className="ml-2 text-2xl">✨</span>
                                            </h2>
                                            <div className="bg-white p-8 rounded-xl shadow-md">
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                <div>
                                                  <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
                                                  <ul className="list-disc pl-8 space-y-3">
                                                    <li className="flex items-start">
                                                      <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                                                      <span>Generate standardized legal names</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                      <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                                                      <span>Support for multiple entity types</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                      <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                                                      <span>Jurisdiction-specific naming</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                      <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                                                      <span>Business type integration</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                      <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                                                      <span>Legal compliance validation</span>
                                                    </li>
                                                  </ul>
                                                </div>
                                                <div>
                                                  <h3 className="text-xl font-semibold text-green-600 mb-4">Benefits</h3>
                                                  <ul className="list-disc pl-8 space-y-3">
                                                    <li className="flex items-start">
                                                      <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                                                      <span>Consistent legal references</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                      <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                                                      <span>Improved legal compliance</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                      <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                                                      <span>Clear business identification</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                      <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                                                      <span>Enhanced legal documentation</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                      <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                                                      <span>Standardized business naming</span>
                                                    </li>
                                                  </ul>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                        
                                          <div className="calculator-section mt-24">
                                            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                                              Understanding Legal Naming <span className="ml-2 text-2xl">📚</span>
                                            </h2>
                                            <div className="bg-white p-8 rounded-xl shadow-md">
                                              <p className="text-gray-700 mb-6">
                                                Legal naming is a systematic approach to creating standardized names for businesses and organizations. It involves careful consideration of various components to ensure the names are accurate, consistent, and legally compliant.
                                              </p>
                                              <ul className="list-none pl-8 space-y-4">
                                                <li className="flex items-center">
                                                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
                                                  <span className="font-medium">Entity Type:</span> Official legal structure (e.g., LLC, Inc., Ltd.)
                                                </li>
                                                <li className="flex items-center">
                                                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
                                                  <span className="font-medium">Jurisdiction:</span> Legal authority and location
                                                </li>
                                                <li className="flex items-center">
                                                  <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
                                                  <span className="font-medium">Purpose:</span> Business objective
                                                </li>
                                                <li className="flex items-center">
                                                  <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
                                                  <span className="font-medium">Owner Name:</span> Legal name of the business owner
                                                </li>
                                                <li className="flex items-center">
                                                  <span className="h-3 w-3 bg-purple-600 rounded-full mr-2"></span>
                                                  <span className="font-medium">Business Type:</span> Nature of the business
                                                </li>
                                              </ul>
                                              <p className="italic text-gray-500 mt-6">
                                                Note: Legal names are typically used in official documents, contracts, and regulatory filings to maintain consistent business identification.
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    };
                        
                                    export default LegalNamingCalculator;
